import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  mapRuleSet,
  mapRuleDefinitionToRow,
  ruleDefinitionToRuleState,
  emptyRuleForSet,
  buildRuleJSON,
} from "./engine";
import {
  getRulesCatalog,
  listRuleSets,
  listRuleDefinitions,
  getRuleDefinition,
  createRuleDefinition,
  updateRuleDefinition,
  deactivateRuleDefinition,
  createRuleSetWizard,
  wizardDraftToApiBody,
} from "./billing-rules-api";
import NewRuleSetWizard from "./NewRuleSetWizard";
import RuleBuilderScreen from "./RuleBuilderScreen";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import {
  Btn, Flash, Input, KpiTile, PageHeader, DataTable, RuleTypeChip, Select,
  cardStyle, C, LoadingBlock,
} from "./ui";
import SvgIcon from "../ipd-billing/SvgIcon";

const TAX_ROWS = [
  { label: "Healthcare GST", value: "Exempt" },
  { label: "Pharmacy GST", value: "5% / 12%" },
  { label: "Cosmetic Procedures", value: "18%" },
  { label: "TDS on Insurance", value: "10%" },
];

function RuleSetsView({
  sets, loading, q, setQ, filterDomain, setFilterDomain, onOpenSet, onNewSet, flash, onDismissFlash,
}) {
  const domains = useMemo(() => Array.from(new Set(sets.map((s) => s.domain))), [sets]);
  const filtered = useMemo(() => sets.filter((s) =>
    (filterDomain === "all" || s.domain === filterDomain) &&
    (!q || s.code.toLowerCase().includes(q.toLowerCase()) || s.name.toLowerCase().includes(q.toLowerCase())),
  ), [sets, filterDomain, q]);

  const stats = useMemo(() => ({
    total: sets.length,
    active: sets.filter((s) => s.active).length,
    domains: domains.length,
  }), [sets, domains]);

  return (
    <div>
      {flash && <div style={{ marginBottom: 16 }}><Flash message={flash.message} tone={flash.tone} onDismiss={onDismissFlash} /></div>}

      <PageHeader
        title="Hospital Billing Rule Sets"
        subtitle="Encounter, payer, package, time-based & approval logic"
        actions={(
          <Btn onClick={onNewSet}>
            <SvgIcon name="plus" size={14} color="#fff" /> New Rule Set
          </Btn>
        )}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
        <KpiTile icon="pricing" label="Rule sets" value={stats.total} tone="primary" />
        <KpiTile icon="check" label="Active" value={stats.active} tone="success" />
        <KpiTile icon="building" label="Domains" value={stats.domains} tone="info" />
        <KpiTile icon="sparkle" label="AI suggestions" value={2} tone="teal" hint="Senior · TPA tariff" />
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 200px", maxWidth: 320 }}>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search code or name…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SvgIcon name="search" size={14} color={C.textSub} />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <Select value={filterDomain} onChange={(e) => setFilterDomain(e.target.value)} width={180}>
            <MenuItem value="all">All domains</MenuItem>
            {domains.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </Select>
        </div>
        {loading ? (
          <LoadingBlock message="Loading rule sets…" />
        ) : (
          <DataTable
            emptyText="No rule sets match this filter."
            columns={[
              { key: "code", label: "Code", render: (s) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{s.code}</span> },
              { key: "name", label: "Name", render: (s) => <span style={{ fontWeight: 600 }}>{s.name}</span> },
              { key: "domain", label: "Domain", render: (s) => <span style={{ color: C.textSub }}>{s.domain}</span> },
              { key: "priority", label: "Priority", render: (s) => s.priority },
              { key: "from", label: "Effective From", render: (s) => s.effective_from },
              {
                key: "status",
                label: "Status",
                render: (s) => (
                  <span style={{
                    padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                    background: s.active ? "#d1f2db" : "#f1f5f9",
                    color: s.active ? "#145a32" : C.textSub,
                  }}>
                    {s.active ? "Active" : "Inactive"}
                  </span>
                ),
              },
              {
                key: "actions",
                label: "Actions",
                align: "right",
                render: (s) => <Btn variant="outline" size="sm" onClick={() => onOpenSet(s)}>Open</Btn>,
              },
            ]}
            rows={filtered.map((s) => ({ ...s, id: s.id }))}
          />
        )}
      </div>

      <div style={{ ...cardStyle, padding: 20 }}>
        <h4 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: C.text }}>Tax & Compliance</h4>
        {TAX_ROWS.map((row, i) => (
          <div
            key={row.label}
            style={{
              display: "flex", justifyContent: "space-between", padding: "10px 0",
              borderBottom: i < TAX_ROWS.length - 1 ? `1px solid ${C.border}` : "none",
            }}
          >
            <span style={{ fontSize: 13, color: C.textSub }}>{row.label}</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RuleListView({
  activeSet, rows, loading, busyId, flash, onDismissFlash, onBack, onNew, onEdit, onDuplicate, onDelete,
}) {
  return (
    <div>
      {flash && <div style={{ marginBottom: 16 }}><Flash message={flash.message} tone={flash.tone} onDismiss={onDismissFlash} /></div>}
      <PageHeader
        title={activeSet.name}
        subtitle={`${activeSet.code} · ${rows.length} rule${rows.length === 1 ? "" : "s"}`}
        back="All rule sets"
        onBack={onBack}
        actions={(
          <Btn onClick={onNew}>
            <SvgIcon name="plus" size={14} color="#fff" /> New Rule
          </Btn>
        )}
      />
      {loading ? (
        <LoadingBlock message="Loading rules…" />
      ) : (
        <DataTable
          emptyText="No rules yet. Create your first rule."
          columns={[
            { key: "code", label: "Code", render: (r) => <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.rule.rule_code}</span> },
            { key: "name", label: "Name", render: (r) => <span style={{ fontWeight: 600 }}>{r.rule.rule_name}</span> },
            { key: "type", label: "Type", render: (r) => <RuleTypeChip type={r.rule.rule_type} /> },
            { key: "priority", label: "Priority", render: (r) => r.rule.priority },
            { key: "from", label: "Effective", render: (r) => r.rule.effective_from },
            { key: "stop", label: "Stop", render: (r) => (r.rule.stop_processing ? "Yes" : "No") },
            { key: "active", label: "Active", render: (r) => (r.rule.is_active ? "Yes" : "No") },
            {
              key: "actions",
              label: "Actions",
              align: "right",
              render: (r) => (
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <Btn variant="outline" size="sm" disabled={busyId === r.id} onClick={() => onEdit(r)}>Edit</Btn>
                  <Btn variant="ghost" size="sm" disabled={!!busyId} onClick={() => onDuplicate(r)}>Copy</Btn>
                  <Btn variant="danger" size="sm" disabled={busyId === r.id} onClick={() => onDelete(r)}>
                    <SvgIcon name="trash" size={14} color={C.danger} />
                  </Btn>
                </div>
              ),
            },
          ]}
          rows={rows.map((r) => ({ ...r, id: r.id }))}
        />
      )}
    </div>
  );
}

export default function PricingRuleEngine() {
  const [screen, setScreen] = useState("ruleSets");
  const [sets, setSets] = useState([]);
  const [rules, setRules] = useState([]);
  const [activeSet, setActiveSet] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [flash, setFlash] = useState(null);
  const [q, setQ] = useState("");
  const [filterDomain, setFilterDomain] = useState("all");
  const [catalog, setCatalog] = useState(null);
  const [loadingSets, setLoadingSets] = useState(true);
  const [loadingRules, setLoadingRules] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [wizardBusy, setWizardBusy] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);

  const showFlash = useCallback((message, tone = "success") => {
    setFlash({ message, tone });
    setTimeout(() => setFlash(null), 3500);
  }, []);

  const dismissFlash = () => setFlash(null);

  const loadCatalog = useCallback(async (ruleDomain) => {
    try {
      const data = await getRulesCatalog(ruleDomain);
      setCatalog(data.catalog ?? data);
    } catch (err) {
      console.error("Catalog load failed:", err);
    }
  }, []);

  const loadRuleSets = useCallback(async () => {
    setLoadingSets(true);
    try {
      const items = await listRuleSets();
      setSets(items.map(mapRuleSet));
    } catch (err) {
      showFlash(err.message || "Failed to load rule sets", "error");
    } finally {
      setLoadingSets(false);
    }
  }, [showFlash]);

  const loadRuleDefinitions = useCallback(async (setId) => {
    setLoadingRules(true);
    try {
      const items = await listRuleDefinitions(setId);
      setRules(items.filter((i) => i.is_active !== false).map(mapRuleDefinitionToRow));
    } catch (err) {
      showFlash(err.message || "Failed to load rules", "error");
      setRules([]);
    } finally {
      setLoadingRules(false);
    }
  }, [showFlash]);

  useEffect(() => {
    loadCatalog();
    loadRuleSets();
  }, [loadCatalog, loadRuleSets]);

  useEffect(() => {
    if (screen === "ruleList" && activeSet?.id) {
      // Re-read the catalog for THIS set's domain so the condition picker only offers
      // fields the set's evaluator can read.
      loadCatalog(activeSet.domain);
      loadRuleDefinitions(activeSet.id);
    }
  }, [screen, activeSet?.id, activeSet?.domain, loadCatalog, loadRuleDefinitions]);

  const openSet = (s) => {
    setActiveSet(s);
    setScreen("ruleList");
  };

  const openNewRule = () => {
    if (!activeSet) return;
    setEditingRule({
      setId: activeSet.id,
      ruleDefinitionId: null,
      rule: emptyRuleForSet(activeSet.id),
    });
    setScreen("builder");
  };

  const openEditRule = async (row) => {
    setRowBusyId(row.id);
    setLoadingEdit(true);
    try {
      const item = await getRuleDefinition(row.id);
      setEditingRule({
        setId: activeSet.id,
        ruleDefinitionId: row.id,
        rule: ruleDefinitionToRuleState(item),
      });
      setScreen("builder");
    } catch (err) {
      showFlash(err.message || "Failed to load rule", "error");
    } finally {
      setRowBusyId(null);
      setLoadingEdit(false);
    }
  };

  const createSet = async (draft) => {
    setWizardBusy(true);
    try {
      const res = await createRuleSetWizard(wizardDraftToApiBody(draft));
      const rs = res.rule_set;
      const mapped = mapRuleSet(rs);
      setSets((prev) => [mapped, ...prev]);
      setActiveSet(mapped);
      setWizardOpen(false);
      setScreen("ruleList");
      showFlash(res.message || `Rule set ${mapped.code} created`);
      await loadRuleDefinitions(mapped.id);
    } catch (err) {
      showFlash(err.message || "Failed to create rule set", "error");
    } finally {
      setWizardBusy(false);
    }
  };

  const saveRule = async (rule) => {
    if (!editingRule) return;
    setSavingRule(true);
    try {
      const body = buildRuleJSON(rule);
      if (editingRule.ruleDefinitionId) {
        await updateRuleDefinition(editingRule.ruleDefinitionId, body);
        showFlash(`Rule ${rule.rule_code} updated`);
      } else {
        await createRuleDefinition(body);
        showFlash(`Rule ${rule.rule_code} created`);
      }
      setScreen("ruleList");
      setEditingRule(null);
      if (activeSet) await loadRuleDefinitions(activeSet.id);
    } catch (err) {
      showFlash(err.message || "Failed to save rule", "error");
    } finally {
      setSavingRule(false);
    }
  };

  const duplicateRule = async (row) => {
    setRowBusyId(row.id);
    try {
      const item = await getRuleDefinition(row.id);
      const state = ruleDefinitionToRuleState(item);
      state.rule_code = `${state.rule_code}-COPY`;
      state.rule_name = `${state.rule_name} (copy)`;
      await createRuleDefinition(buildRuleJSON(state));
      showFlash("Rule duplicated");
      if (activeSet) await loadRuleDefinitions(activeSet.id);
    } catch (err) {
      showFlash(err.message || "Failed to duplicate rule", "error");
    } finally {
      setRowBusyId(null);
    }
  };

  const deleteRule = async (row) => {
    setRowBusyId(row.id);
    try {
      await deactivateRuleDefinition(row.id);
      setRules((prev) => prev.filter((r) => r.id !== row.id));
      showFlash("Rule deactivated", "warning");
    } catch (err) {
      showFlash(err.message || "Failed to deactivate rule", "error");
    } finally {
      setRowBusyId(null);
    }
  };

  if (loadingEdit) {
    return <LoadingBlock message="Loading rule…" />;
  }

  if (screen === "builder" && editingRule) {
    const setName = sets.find((s) => s.id === editingRule.setId)?.name ?? activeSet?.name ?? "Rule Set";
    return (
      <div>
        {flash && <div style={{ marginBottom: 16 }}><Flash message={flash.message} tone={flash.tone} onDismiss={dismissFlash} /></div>}
        <RuleBuilderScreen
          initial={editingRule.rule}
          setName={setName}
          catalog={catalog}
          saving={savingRule}
          onBack={() => { setScreen("ruleList"); setEditingRule(null); }}
          onSave={saveRule}
          flash={showFlash}
        />
      </div>
    );
  }

  if (screen === "ruleList" && activeSet) {
    return (
      <RuleListView
        activeSet={activeSet}
        rows={rules}
        loading={loadingRules}
        busyId={rowBusyId}
        flash={flash}
        onDismissFlash={dismissFlash}
        onBack={() => { setScreen("ruleSets"); setActiveSet(null); setRules([]); }}
        onNew={openNewRule}
        onEdit={openEditRule}
        onDuplicate={duplicateRule}
        onDelete={deleteRule}
      />
    );
  }

  return (
    <>
      <RuleSetsView
        sets={sets}
        loading={loadingSets}
        q={q}
        setQ={setQ}
        filterDomain={filterDomain}
        setFilterDomain={setFilterDomain}
        onOpenSet={openSet}
        onNewSet={() => setWizardOpen(true)}
        flash={flash}
        onDismissFlash={dismissFlash}
      />
      <NewRuleSetWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        existingCodes={sets.map((s) => s.code)}
        catalog={catalog}
        creating={wizardBusy}
        onCreate={createSet}
      />
    </>
  );
}
